import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  Divider,
  TextField as MuiTextField,
  Alert as MuiAlert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import axios from "axios";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { GlobalContext } from "../contexts/GlobalContext";
import { usePermissions } from "../hooks/usePermissions";
import { CAPABILITIES } from "../auth/permissions";

const Card = styled(MuiCard)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

// Helper function to get user's locale decimal separator
const getDecimalSeparator = () => {
  try {
    // Use Intl.NumberFormat to detect the decimal separator for the user's locale
    const numberFormat = new Intl.NumberFormat(navigator.language || "en-US");
    const parts = numberFormat.formatToParts(1.5);
    const decimalPart = parts.find((part) => part.type === "decimal");
    return decimalPart ? decimalPart.value : ".";
  } catch (error) {
    // Fallback to dot if detection fails
    return ".";
  }
};

// Helper function to convert comma to dot and parse to float
const parseDecimalValue = (value) => {
  if (!value || value === "") return 0;
  const normalizedValue = String(value).replace(",", ".");
  return parseFloat(normalizedValue) || 0;
};

// Helper function to normalize input (allow both comma and dot)
const normalizeDecimalInput = (value) => {
  if (!value) return "";
  return String(value).replace(",", ".");
};

// Helper function to format value with 2 decimals using user's locale separator
const formatWithTwoDecimals = (value) => {
  if (!value && value !== 0) {
    const separator = getDecimalSeparator();
    return `0${separator}00`;
  }
  const numValue = typeof value === "string" ? parseDecimalValue(value) : value;
  const formatted = numValue.toFixed(2);
  const separator = getDecimalSeparator();
  // Replace dot with user's locale separator
  return separator === "," ? formatted.replace(".", ",") : formatted;
};

export default function ViewRefundRatesDialog(props) {
  const { viewRefundRatesDialog, setViewRefundRatesDialog, refreshTable, countryData } = props;
  const { globalData } = useContext(GlobalContext);
  const { hasPermission } = usePermissions();
  const canWriteRefundRates = hasPermission(CAPABILITIES.refundRatesWrite, "W");
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitInError, setSubmitInError] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [newRate, setNewRate] = useState({ fromRate: "", toRate: "", monthFee: "" });

  useEffect(() => {
    if (viewRefundRatesDialog.isOpen && viewRefundRatesDialog.countryId) {
      fetchRates();
    }
  }, [viewRefundRatesDialog.isOpen, viewRefundRatesDialog.countryId, viewRefundRatesDialog.productId]);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/refundrates/country/${viewRefundRatesDialog.countryId}`);
      const fetchedRates = response.data || [];
      // Filter by productId if specified
      const filteredRates = viewRefundRatesDialog.productId 
        ? fetchedRates.filter(rate => rate.productId === viewRefundRatesDialog.productId)
        : fetchedRates;
      setRates(filteredRates);
    } catch (error) {
      console.error("Error fetching rates:", error);
      setErrorMessage("Error fetching refund rates");
      setSubmitInError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRate = async (rateId) => {
    try {
      await axios.delete(`/api/v1/refundrates/${rateId}`);
      setErrorMessage("Rate deleted successfully");
      setSubmitInError(false);
      fetchRates();
      refreshTable();
    } catch (error) {
      console.error("Error deleting rate:", error);
      setErrorMessage("Error deleting rate");
      setSubmitInError(true);
    }
  };


  const handleUpdateRate = async (rateId, fromRate, toRate, monthFee) => {
    if (!globalData?.userId) return;

    try {
      setLoading(true);
      const rate = rates.find((r) => r.id === rateId);
      const normalizedFromRate = parseDecimalValue(fromRate);
      const normalizedToRate = parseDecimalValue(toRate);
      const normalizedMonthFee = parseDecimalValue(monthFee);
      const normalizedQuarterFee = normalizedMonthFee * 3;
      const normalizedYearFee = normalizedMonthFee * 12;
      await axios.put(`/api/v1/refundrates/${rateId}`, {
        countryId: rate.countryId,
        productId: rate.productId,
        fromRate: normalizedFromRate,
        toRate: normalizedToRate,
        monthFee: normalizedMonthFee,
        quarterFee: normalizedQuarterFee,
        yearFee: normalizedYearFee,
        lastModifiedBy: globalData.userId,
      });
      setEditingRate(null);
      setErrorMessage("Rate updated successfully");
      setSubmitInError(false);
      fetchRates();
      refreshTable();
    } catch (error) {
      console.error("Error updating rate:", error);
      setErrorMessage("Error updating rate");
      setSubmitInError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = async () => {
    if (!viewRefundRatesDialog.countryId || !globalData?.userId) return;

    try {
      setLoading(true);
      const normalizedFromRate = parseDecimalValue(newRate.fromRate);
      const normalizedToRate = parseDecimalValue(newRate.toRate);
      const normalizedMonthFee = parseDecimalValue(newRate.monthFee || "0");
      const normalizedQuarterFee = normalizedMonthFee * 3;
      const normalizedYearFee = normalizedMonthFee * 12;
      await axios.post("/api/v1/refundrates", {
        countryId: viewRefundRatesDialog.countryId,
        productId: viewRefundRatesDialog.productId,
        fromRate: normalizedFromRate,
        toRate: normalizedToRate,
        monthFee: normalizedMonthFee,
        quarterFee: normalizedQuarterFee,
        yearFee: normalizedYearFee,
        createdBy: globalData.userId,
      });
      setNewRate({ fromRate: "", toRate: "", monthFee: "" });
      setErrorMessage("Rate added successfully");
      setSubmitInError(false);
      fetchRates();
      refreshTable();
    } catch (error) {
      console.error("Error adding rate:", error);
      setErrorMessage("Error adding rate");
      setSubmitInError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={viewRefundRatesDialog.isOpen}
      maxWidth="md"
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{viewRefundRatesDialog.title}</DialogTitle>
      <Divider />
      <DialogContent>
        <Card mb={6}>
          <CardContent>
            {submitInError ? (
              <Alert severity="error" my={3}>
                {errorMessage}
              </Alert>
            ) : errorMessage.length > 0 && !submitInError ? (
              <Alert severity="success" my={3}>
                {errorMessage}
              </Alert>
            ) : (
              ""
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" my={20} mx={40}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Refund Rates
                </Typography>

                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    mt: 2,
                    maxHeight: "400px",
                    overflowY: "auto"
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>From Rate</TableCell>
                        <TableCell>To Rate</TableCell>
                        <TableCell>Month Fee</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rates.map((rate) => (
                        <TableRow key={rate.id}>
                          <TableCell>
                            {editingRate?.id === rate.id ? (
                              <TextField
                                type="text"
                                size="small"
                                value={editingRate.fromRate}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow numbers, comma, and dot
                                  if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                    setEditingRate({
                                      ...editingRate,
                                      fromRate: value,
                                    });
                                  }
                                }}
                                onBlur={(e) => {
                                  const normalized = parseDecimalValue(e.target.value);
                                  setEditingRate({
                                    ...editingRate,
                                    fromRate: formatWithTwoDecimals(normalized),
                                  });
                                }}
                              />
                            ) : (
                              formatWithTwoDecimals(rate.fromRate)
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRate?.id === rate.id ? (
                              <TextField
                                type="text"
                                size="small"
                                value={editingRate.toRate}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow numbers, comma, and dot
                                  if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                    setEditingRate({
                                      ...editingRate,
                                      toRate: value,
                                    });
                                  }
                                }}
                                onBlur={(e) => {
                                  const normalized = parseDecimalValue(e.target.value);
                                  setEditingRate({
                                    ...editingRate,
                                    toRate: formatWithTwoDecimals(normalized),
                                  });
                                }}
                              />
                            ) : (
                              formatWithTwoDecimals(rate.toRate)
                            )}
                          </TableCell>
                          <TableCell>
                            {editingRate?.id === rate.id ? (
                              <TextField
                                type="text"
                                size="small"
                                value={editingRate.monthFee}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Allow numbers, comma, and dot
                                  if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                    setEditingRate({
                                      ...editingRate,
                                      monthFee: value,
                                    });
                                  }
                                }}
                                onBlur={(e) => {
                                  const normalized = parseDecimalValue(e.target.value);
                                  setEditingRate({
                                    ...editingRate,
                                    monthFee: formatWithTwoDecimals(normalized),
                                  });
                                }}
                              />
                            ) : (
                              formatWithTwoDecimals(rate.monthFee || 0)
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {editingRate?.id === rate.id ? (
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={() => {
                                    handleUpdateRate(rate.id, editingRate.fromRate, editingRate.toRate, editingRate.monthFee);
                                  }}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setEditingRate(null)}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            ) : canWriteRefundRates ? (
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    setEditingRate({
                                      id: rate.id,
                                      fromRate: formatWithTwoDecimals(rate.fromRate),
                                      toRate: formatWithTwoDecimals(rate.toRate),
                                      monthFee: formatWithTwoDecimals(rate.monthFee || 0),
                                    })
                                  }
                                >
                                  Edit
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteRate(rate.id)}
                                  color="error"
                                  aria-label="Delete refund rate"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                      {canWriteRefundRates && (
                      <TableRow>
                          <TableCell>
                            <TextField
                              type="text"
                              size="small"
                              placeholder="From Rate"
                              value={newRate.fromRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow numbers, comma, and dot
                                if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                  setNewRate({ ...newRate, fromRate: value });
                                }
                              }}
                              onBlur={(e) => {
                                const normalized = parseDecimalValue(e.target.value);
                                setNewRate({ ...newRate, fromRate: formatWithTwoDecimals(normalized) });
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="text"
                              size="small"
                              placeholder="To Rate"
                              value={newRate.toRate}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow numbers, comma, and dot
                                if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                  setNewRate({ ...newRate, toRate: value });
                                }
                              }}
                              onBlur={(e) => {
                                const normalized = parseDecimalValue(e.target.value);
                                setNewRate({ ...newRate, toRate: formatWithTwoDecimals(normalized) });
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="text"
                              size="small"
                              placeholder="Month Fee"
                              value={newRate.monthFee}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Allow numbers, comma, and dot
                                if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                  setNewRate({ ...newRate, monthFee: value });
                                }
                              }}
                              onBlur={(e) => {
                                const normalized = parseDecimalValue(e.target.value);
                                setNewRate({ ...newRate, monthFee: formatWithTwoDecimals(normalized) });
                              }}
                            />
                          </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={handleAddRate}
                            color="primary"
                            disabled={!newRate.fromRate || !newRate.toRate}
                          >
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    mt={3}
                    my={2}
                    onClick={() =>
                      setViewRefundRatesDialog({
                        ...viewRefundRatesDialog,
                        isOpen: false,
                      })
                    }
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

